import { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar, Toolbar, Typography, Drawer, List, ListItemButton,
  ListItemIcon, ListItemText, IconButton, Box, CssBaseline,
  Avatar, Badge, InputBase, Tooltip, Menu, MenuItem, Divider,
  CircularProgress, Popover, Chip, Paper, ClickAwayListener,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  useMediaQuery, useTheme,
} from "@mui/material";

import MenuIcon                      from "@mui/icons-material/Menu";
import DashboardIcon                 from "@mui/icons-material/Dashboard";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SearchIcon                    from "@mui/icons-material/Search";
import InventoryIcon                 from "@mui/icons-material/Inventory";
import MeetingRoomIcon               from "@mui/icons-material/MeetingRoom";
import LogoutIcon                    from "@mui/icons-material/Logout";
import AssignmentOutlinedIcon        from "@mui/icons-material/AssignmentOutlined";
import CheckCircleOutlineIcon        from "@mui/icons-material/CheckCircleOutline";
import DoneAllIcon                   from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon        from "@mui/icons-material/AssignmentTurnedIn";
import AddBoxOutlinedIcon            from "@mui/icons-material/AddBoxOutlined";
import InfoOutlinedIcon              from "@mui/icons-material/InfoOutlined";
import PeopleAltIcon                 from "@mui/icons-material/PeopleAlt";
import BarChartIcon                  from "@mui/icons-material/BarChart";
import HistoryIcon                   from "@mui/icons-material/History";
import EditNoteIcon                  from "@mui/icons-material/EditNote";
import CategoryIcon                  from "@mui/icons-material/Category";
import StorefrontIcon                from "@mui/icons-material/Storefront";
import PersonOutlinedIcon            from "@mui/icons-material/PersonOutlined";
import DomainIcon                    from "@mui/icons-material/Domain";

import echo from "../services/echo";

const API_BASE        = "";
const drawerWidth     = 260;
const miniDrawerWidth = 64;
const POLL_INTERVAL   = 30_000;

// ── Semua path pakai /superadmin ──────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: "Navigasi",
    items: [
      { label: "Dashboard", icon: <DashboardIcon fontSize="small" />, to: "/superadmin" },
    ],
  },
  {
    label: "Aset & Barang",
    items: [
      { label: "Daftar Barang", icon: <InventoryIcon fontSize="small" />,  to: "/superadmin/items" },
      { label: "Daftar ATK",    icon: <EditNoteIcon fontSize="small" />,    to: "/superadmin/atk/items" },
      { label: "Ruangan",       icon: <MeetingRoomIcon fontSize="small" />, to: "/superadmin/rooms" },
    ],
  },
  {
    label: "Pengadaan",
    items: [
      { label: "Sarana Prasarana", icon: <AssignmentOutlinedIcon fontSize="small" />, to: "/superadmin/procurement" },
      { label: "ATK",              icon: <EditNoteIcon fontSize="small" />,            to: "/superadmin/atk" },
    ],
  },
  {
    label: "Laporan",
    items: [
      { label: "Laporan Pengadaan", icon: <BarChartIcon fontSize="small" />, to: "/superadmin/laporan" },
    ],
  },
  {
    label: "Manajemen",
    items: [
      { label: "Kelola User",   icon: <PeopleAltIcon fontSize="small" />, to: "/superadmin/users" },
      { label: "Log Aktivitas", icon: <HistoryIcon fontSize="small" />,   to: "/superadmin/log-aktivitas" },
    ],
  },
];

const TYPE_META = {
  "Barang":        { color: "#1677ff", bg: "#e8f4fd", icon: <InventoryIcon sx={{ fontSize: 14 }} /> },
  "ATK":           { color: "#fa8c16", bg: "#fff7e6", icon: <EditNoteIcon sx={{ fontSize: 14 }} /> },
  "Pengadaan ATK": { color: "#722ed1", bg: "#f9f0ff", icon: <StorefrontIcon sx={{ fontSize: 14 }} /> },
  "Pengadaan":     { color: "#52c41a", bg: "#f6ffed", icon: <AssignmentOutlinedIcon sx={{ fontSize: 14 }} /> },
  "Ruangan":       { color: "#13c2c2", bg: "#e6fffb", icon: <DomainIcon sx={{ fontSize: 14 }} /> },
  "User":          { color: "#eb2f96", bg: "#fff0f6", icon: <PersonOutlinedIcon sx={{ fontSize: 14 }} /> },
};

function getNotifIcon(type) {
  switch (type) {
    case "procurement_new":    return <AddBoxOutlinedIcon     sx={{ fontSize: 18, color: "#1677ff" }} />;
    case "procurement_status": return <AssignmentTurnedInIcon sx={{ fontSize: 18, color: "#52c41a" }} />;
    case "item_added":         return <InventoryIcon          sx={{ fontSize: 18, color: "#fa8c16" }} />;
    default:                   return <InfoOutlinedIcon       sx={{ fontSize: 18, color: "#8c8c8c" }} />;
  }
}

function getNotifBg(type) {
  switch (type) {
    case "procurement_new":    return "#e8f4fd";
    case "procurement_status": return "#f6ffed";
    case "item_added":         return "#fff7e6";
    default:                   return "#f5f5f5";
  }
}

function timeAgo(dateStr) {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return "Baru saja";
  if (diff < 3600)  return `${Math.floor(diff / 60)} mnt lalu`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} jam lalu`;
  return `${Math.floor(diff / 86400)} hari lalu`;
}

export default function SuperadminLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [notifAnchor,   setNotifAnchor]   = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [notifLoading,  setNotifLoading]  = useState(false);
  const pollingRef = useRef(null);

  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const searchRef   = useRef(null);
  const debounceRef = useRef(null);

  const token = localStorage.getItem("token");
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")) || null; }
    catch { return null; }
  })();
  const userId = user?.id;

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); setSearchOpen(false); return; }
    setSearchLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/search?q=${encodeURIComponent(q)}`, { headers: authHeaders });
      const json = await res.json();
      setSearchResults(json.results || []);
      setSearchOpen(true);
    } catch (e) { console.error("Search error:", e); }
    finally { setSearchLoading(false); }
  }, [token]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleSearchClear = () => { setSearchQuery(""); setSearchResults([]); setSearchOpen(false); };

  const groupedResults = searchResults.reduce((acc, item) => {
    if (!acc[item.type]) acc[item.type] = [];
    acc[item.type].push(item);
    return acc;
  }, {});

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/notifications/unread-count`, { headers: authHeaders });
      const json = await res.json();
      if (json.success) setUnreadCount(json.unread_count);
    } catch (e) { console.error("Poll notif error:", e); }
  }, [token]);

  useEffect(() => {
    fetchUnreadCount();
    pollingRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    return () => clearInterval(pollingRef.current);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!userId) return;
    const channel = echo.private(`notifications.${userId}`);
    channel.listen(".notification.sent", (data) => {
      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((prev) => prev + 1);
      if (typeof window !== "undefined" && "Notification" in window && window.Notification.permission === "granted") {
        new window.Notification(data.title, { body: data.message, icon: "/lpkia-logo2.png" });
      }
    });
    return () => { echo.leave(`notifications.${userId}`); };
  }, [userId]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/notifications?limit=30`, { headers: authHeaders });
      const json = await res.json();
      if (json.success) { setNotifications(json.data); setUnreadCount(json.unread_count); }
    } catch (e) { console.error("Fetch notif error:", e); }
    finally { setNotifLoading(false); }
  };

  const handleNotifOpen  = (e) => { setNotifAnchor(e.currentTarget); fetchNotifications(); };
  const handleNotifClose = ()  => setNotifAnchor(null);

  const handleMarkRead = async (notif) => {
    if (notif.is_read) return;
    try {
      await fetch(`${API_BASE}/api/notifications/${notif.id}/read`, { method: "PUT", headers: authHeaders });
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) { console.error(e); }
  };

  const handleMarkAllRead = async () => {
    try {
      await fetch(`${API_BASE}/api/notifications/mark-all-read`, { method: "PUT", headers: authHeaders });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) { console.error(e); }
  };

  const handleNotifClick = (notif) => {
    handleMarkRead(notif);
    if (notif.type === "procurement_new" || notif.type === "procurement_status") {
      const id = notif.data?.procurement_id;
      navigate(id ? `/superadmin/procurement/${id}` : "/superadmin/procurement");
    }
    handleNotifClose();
  };

  const handleAvatarClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose   = ()  => setAnchorEl(null);

  const handleLogoutClick = () => {
    handleMenuClose();
    setLogoutDialogOpen(true);
  };

  const confirmLogout = async () => {
    setLoggingOut(true);
    try {
      await fetch(`${API_BASE}/api/logout`, { method: "POST", headers: authHeaders });
    } catch (e) { console.error("Logout error:", e); }
    finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setLoggingOut(false);
      setLogoutDialogOpen(false);
      navigate("/admin/login");
    }
  };

  const userInitials = user?.name
    ? user.name.split(" ").slice(0, 2).map((n) => n[0]).join("").toUpperCase()
    : "SA";

  const isActive = (path) => {
    if (path === "/superadmin") return location.pathname === "/superadmin";
    if (location.pathname === path) return true;
    const allNavPaths = NAV_GROUPS.flatMap(g => g.items.map(i => i.to));
    const hasChildInNav = allNavPaths.some(p => p !== path && p.startsWith(path + "/"));
    if (hasChildInNav) return false;
    return location.pathname.startsWith(path + "/");
  };

  const notifOpen = Boolean(notifAnchor);

  return (
    <Box sx={{ display: "flex", backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <CssBaseline />

      {/* SIDEBAR */}
      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={open}
        onClose={() => isMobile && setOpen(false)}
        sx={{
          width: open ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          transition: "width 0.25s ease",
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : (isMobile ? 0 : miniDrawerWidth),
            backgroundColor: "#fff",
            borderRight: "1px solid #e6ebf1",
            boxSizing: "border-box",
            overflowX: "hidden",
            transition: "width 0.25s ease",
          },
        }}
      >
        <Box sx={{
          px: open ? 3 : 0, height: 60,
          display: "flex", alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          gap: 1.5, borderBottom: "1px solid #e6ebf1", flexShrink: 0,
        }}>
          <Box component="img" src="/lpkia-logo2.png" alt="LPKIA"
            sx={{ height: open ? 44 : 36, width: "auto", objectFit: "contain", flexShrink: 0 }} />
          {open && (
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                BAS-LPKIA
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "#8c8c8c", whiteSpace: "nowrap", lineHeight: 1.2 }}>
                Sistem Pengadaan
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ overflowY: "auto", overflowX: "hidden", flexGrow: 1 }}>
          {NAV_GROUPS.map((group) => (
            <Box key={group.label}>
              {open ? (
                <Box sx={{ px: 3, pt: 2.5, pb: 0.5 }}>
                  <Typography sx={{
                    fontSize: "0.7rem", fontWeight: 600, color: "#8c8c8c",
                    textTransform: "uppercase", letterSpacing: "0.08em", whiteSpace: "nowrap",
                  }}>
                    {group.label}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ height: 12 }} />
              )}
              <List disablePadding sx={{ px: open ? 1.5 : 0.75, pb: 0.5 }}>
                {group.items.map((item) => {
                  const active = isActive(item.to);
                  return (
                    <Tooltip key={item.to} title={!open ? item.label : ""} placement="right" arrow>
                      <Link to={item.to} style={{ textDecoration: "none", color: "inherit" }}>
                        <ListItemButton sx={{
                          borderRadius: "8px", mb: 0.25,
                          px: open ? 1.5 : 1, py: 0.85,
                          justifyContent: open ? "flex-start" : "center",
                          backgroundColor: active ? "#e8f4fd" : "transparent",
                          color: active ? "#1677ff" : "#4a4a4a",
                          "&:hover": { backgroundColor: active ? "#e8f4fd" : "#f5f5f5" },
                        }}>
                          <ListItemIcon sx={{
                            minWidth: open ? 34 : 0,
                            color: active ? "#1677ff" : "#8c8c8c",
                            justifyContent: "center",
                          }}>
                            {item.icon}
                          </ListItemIcon>
                          {open && (
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                fontSize: "0.875rem",
                                fontWeight: active ? 600 : 400,
                                color: "inherit",
                                whiteSpace: "nowrap",
                              }}
                            />
                          )}
                        </ListItemButton>
                      </Link>
                    </Tooltip>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>
      </Drawer>

      {/* CONTENT */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <AppBar position="sticky" elevation={0} sx={{
          backgroundColor: "#fff", borderBottom: "1px solid #e6ebf1",
          color: "#1a1a2e", height: 60, justifyContent: "center", top: 0, zIndex: 1100,
        }}>
          <Toolbar sx={{ gap: 1, minHeight: "60px !important" }}>
            <IconButton onClick={() => setOpen(!open)} size="small" sx={{ color: "#595959", mr: 0.5 }}>
              <MenuIcon fontSize="small" />
            </IconButton>
            {!isMobile && (
              <ClickAwayListener onClickAway={handleSearchClear}>
                <Box ref={searchRef} sx={{ position: "relative", width: 280 }}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    border: "1px solid", borderColor: searchOpen ? "#1677ff" : "#d9d9d9",
                    borderRadius: "8px", px: 1.5, py: 0.6,
                    backgroundColor: "#fafafa", transition: "border-color 0.2s",
                  }}>
                  {searchLoading
                    ? <CircularProgress size={14} sx={{ color: "#8c8c8c", flexShrink: 0 }} />
                    : <SearchIcon sx={{ fontSize: 16, color: "#8c8c8c", flexShrink: 0 }} />
                  }
                  <InputBase
                    placeholder="Cari barang, pengadaan, user..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => searchQuery.length >= 2 && setSearchOpen(true)}
                    sx={{ fontSize: "0.8rem", color: "#595959", width: "100%" }}
                    inputProps={{ "aria-label": "global search" }}
                  />
                  {searchQuery && (
                    <Box component="span" onClick={handleSearchClear}
                      sx={{ fontSize: "0.7rem", color: "#bfbfbf", cursor: "pointer", flexShrink: 0, lineHeight: 1, "&:hover": { color: "#595959" } }}>
                      ✕
                    </Box>
                  )}
                </Box>

                {searchOpen && (
                  <Paper elevation={6} sx={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0,
                    width: 360, borderRadius: "12px", border: "1px solid #e6ebf1",
                    overflow: "hidden", zIndex: 1300, maxHeight: 480, overflowY: "auto",
                  }}>
                    {searchResults.length === 0 && !searchLoading ? (
                      <Box sx={{ py: 4, textAlign: "center" }}>
                        <SearchIcon sx={{ fontSize: 32, color: "#d9d9d9", mb: 1 }} />
                        <Typography sx={{ fontSize: "0.82rem", color: "#8c8c8c" }}>
                          Tidak ada hasil untuk "{searchQuery}"
                        </Typography>
                      </Box>
                    ) : (
                      Object.entries(groupedResults).map(([type, items]) => {
                        const meta = TYPE_META[type] || { color: "#595959", bg: "#f5f5f5", icon: <CategoryIcon sx={{ fontSize: 14 }} /> };
                        return (
                          <Box key={type}>
                            <Box sx={{
                              px: 2, py: 0.8, backgroundColor: "#fafafa",
                              borderBottom: "1px solid #f0f0f0",
                              display: "flex", alignItems: "center", gap: 0.8,
                            }}>
                              <Box sx={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                width: 20, height: 20, borderRadius: "4px",
                                backgroundColor: meta.bg, color: meta.color,
                              }}>
                                {meta.icon}
                              </Box>
                              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: meta.color, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                {type}
                              </Typography>
                              <Chip label={items.length} size="small" sx={{
                                height: 16, fontSize: "0.6rem", fontWeight: 700,
                                backgroundColor: meta.bg, color: meta.color,
                                "& .MuiChip-label": { px: 0.7 },
                              }} />
                            </Box>
                            {items.map((result, idx) => (
                              <Box key={result.id}>
                                <Box
                                  onClick={() => { navigate(result.navigate); handleSearchClear(); }}
                                  sx={{
                                    px: 2, py: 1.2, cursor: "pointer",
                                    display: "flex", alignItems: "center", gap: 1.5,
                                    "&:hover": { backgroundColor: "#f5f5f5" },
                                    transition: "background 0.15s",
                                  }}
                                >
                                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: meta.color, flexShrink: 0 }} />
                                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 500, color: "#1a1a2e", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                      {result.label}
                                    </Typography>
                                    {result.meta && (
                                      <Typography sx={{ fontSize: "0.72rem", color: "#8c8c8c", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {result.meta}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                {idx < items.length - 1 && <Divider sx={{ mx: 2, borderColor: "#f5f5f5" }} />}
                              </Box>
                            ))}
                          </Box>
                        );
                      })
                    )}
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {/* Notif Bell */}
            <IconButton onClick={handleNotifOpen} size="small" sx={{
              color: "#595959", border: "1px solid #e6ebf1", borderRadius: "8px", p: 0.8,
              "&:hover": { backgroundColor: "#f5f5f5" },
              backgroundColor: notifOpen ? "#f0f7ff" : "transparent",
            }}>
              <Badge badgeContent={unreadCount} color="error"
                sx={{ "& .MuiBadge-badge": { fontSize: "0.6rem", minWidth: 16, height: 16 } }}>
                <NotificationsNoneOutlinedIcon fontSize="small" />
              </Badge>
            </IconButton>

            {/* Notif Popover */}
            <Popover
              open={notifOpen} anchorEl={notifAnchor} onClose={handleNotifClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ elevation: 4, sx: { mt: 1.5, width: 380, borderRadius: "12px", border: "1px solid #e6ebf1", overflow: "hidden" } }}
            >
              <Box sx={{
                px: 2.5, py: 1.8, display: "flex", alignItems: "center", justifyContent: "space-between",
                borderBottom: "1px solid #f0f0f0", background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e" }}>Notifikasi</Typography>
                  {unreadCount > 0 && (
                    <Chip label={unreadCount} size="small" sx={{
                      height: 18, fontSize: "0.65rem", fontWeight: 700,
                      backgroundColor: "#ff4d4f", color: "#fff", "& .MuiChip-label": { px: 0.8 },
                    }} />
                  )}
                </Box>
                {unreadCount > 0 && (
                  <Tooltip title="Tandai semua sudah dibaca" arrow>
                    <IconButton size="small" onClick={handleMarkAllRead}
                      sx={{ color: "#1677ff", gap: 0.5, borderRadius: "6px", px: 1, "&:hover": { backgroundColor: "#e8f4fd" } }}>
                      <DoneAllIcon sx={{ fontSize: 16 }} />
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>Baca semua</Typography>
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ maxHeight: 420, overflowY: "auto" }}>
                {notifLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={28} /></Box>
                ) : notifications.length === 0 ? (
                  <Box sx={{ py: 5, textAlign: "center" }}>
                    <NotificationsNoneOutlinedIcon sx={{ fontSize: 40, color: "#d9d9d9", mb: 1 }} />
                    <Typography sx={{ fontSize: "0.85rem", color: "#8c8c8c" }}>Tidak ada notifikasi</Typography>
                  </Box>
                ) : notifications.map((notif, idx) => (
                  <Box key={notif.id}>
                    <Box onClick={() => handleNotifClick(notif)} sx={{
                      px: 2.5, py: 1.8, display: "flex", gap: 1.5, alignItems: "flex-start",
                      cursor: "pointer", position: "relative",
                      backgroundColor: notif.is_read ? "#fff" : "#f0f7ff",
                      "&:hover": { backgroundColor: notif.is_read ? "#fafafa" : "#e8f4fd" },
                    }}>
                      <Box sx={{
                        width: 34, height: 34, borderRadius: "8px", flexShrink: 0,
                        backgroundColor: getNotifBg(notif.type),
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        {getNotifIcon(notif.type)}
                      </Box>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: notif.is_read ? 400 : 600, color: "#1a1a2e", lineHeight: 1.3, mb: 0.3 }}>
                          {notif.title}
                        </Typography>
                        <Typography sx={{
                          fontSize: "0.78rem", color: "#595959", lineHeight: 1.4,
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
                        }}>
                          {notif.message}
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", color: "#bfbfbf", mt: 0.5 }}>
                          {timeAgo(notif.created_at)}
                        </Typography>
                      </Box>
                      {!notif.is_read && (
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#1677ff", flexShrink: 0, mt: 0.5 }} />
                      )}
                      {!notif.is_read && (
                        <Tooltip title="Tandai dibaca" arrow>
                          <IconButton size="small"
                            onClick={(e) => { e.stopPropagation(); handleMarkRead(notif); }}
                            sx={{
                              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                              opacity: 0, transition: "opacity 0.15s", color: "#1677ff", p: 0.4,
                              ".MuiBox-root:hover &": { opacity: 1 },
                            }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    {idx < notifications.length - 1 && <Divider sx={{ mx: 2.5, borderColor: "#f5f5f5" }} />}
                  </Box>
                ))}
              </Box>
              {notifications.length > 0 && (
                <Box sx={{ px: 2.5, py: 1.5, borderTop: "1px solid #f0f0f0", textAlign: "center", backgroundColor: "#fafafa" }}>
                  <Typography
                    sx={{ fontSize: "0.8rem", color: "#1677ff", cursor: "pointer", fontWeight: 500, "&:hover": { textDecoration: "underline" } }}
                    onClick={() => { handleNotifClose(); navigate("/superadmin/procurement"); }}
                  >
                    Lihat semua notifikasi →
                  </Typography>
                </Box>
              )}
            </Popover>

            {/* Avatar */}
            <Tooltip title="Account" arrow>
              <Avatar onClick={handleAvatarClick} sx={{
                width: 32, height: 32, fontSize: "0.75rem", fontWeight: 700,
                background: "linear-gradient(135deg, #2196f3, #1565c0)", cursor: "pointer", ml: 0.5,
              }}>
                {loggingOut ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : userInitials}
              </Avatar>
            </Tooltip>

            <Menu
              anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{ elevation: 3, sx: {
                mt: 1, minWidth: 180, borderRadius: "10px", border: "1px solid #e6ebf1",
                "& .MuiMenuItem-root": { fontSize: "0.875rem", gap: 1.5, py: 1, px: 2, borderRadius: "6px", mx: 0.5 },
              }}}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                  {user?.name || "Super Administrator"}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#8c8c8c" }}>
                  {user?.email || "superadmin@lpkia.ac.id"}
                </Typography>
              </Box>
              <MenuItem onClick={handleLogoutClick} disabled={loggingOut} sx={{ color: "#ff4d4f !important" }}>
                <LogoutIcon fontSize="small" sx={{ color: "#ff4d4f" }} />
                {loggingOut ? "Logging out..." : "Logout"}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3, backgroundColor: "#f8f9fa", flexGrow: 1, minHeight: "calc(100vh - 60px)" }}>
          <Outlet />
        </Box>

        <Box sx={{ borderTop: "1px solid #e5e7eb", backgroundColor: "#fff", py: 2, textAlign: "center" }}>
          <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>
            © {new Date().getFullYear()} BAS-LPKIA · Designed & Developed by{" "}
            <Box component="a" href="https://github.com/rbprmna" target="_blank" rel="noopener noreferrer" sx={{ color: "#6b7280", fontWeight: 600, textDecoration: "none", "&:hover": { color: "#111827", textDecoration: "underline" } }}>
              Robi Permana
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "12px", minWidth: 320 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1a1a2e" }}>
          Konfirmasi Logout
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: "#4a4a4a" }}>
            Apakah Anda yakin ingin keluar dari sistem?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            sx={{ textTransform: "none", color: "#8c8c8c", fontWeight: 600 }}
          >
            Batal
          </Button>
          <Button
            onClick={confirmLogout}
            variant="contained"
            color="error"
            disabled={loggingOut}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "6px" }}
          >
            {loggingOut ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Ya, Logout"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}